import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

/**
 * Upload status for one detail image, keyed by the entry's stable `key`.
 * Lives outside the form (and outside each tile's mount lifecycle) so it
 * survives StrictMode remounts — the upload is fired from the pick event and
 * tracked here, and each tile reads only its own status.
 */
export type DetailImageUploadStatus = 'uploading' | 'error';

type DetailImageUploadContextValue = {
  /** Current status per entry key (absent = not uploading / settled ok). */
  uploadStates: ReadonlyMap<string, DetailImageUploadStatus>;
  setUploading: (key: string) => void;
  setError: (key: string) => void;
  clear: (key: string) => void;
};

const DetailImageUploadContext = createContext<
  DetailImageUploadContextValue | undefined
>(undefined);

export const DetailImageUploadProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [uploadStates, setUploadStates] = useState<
    ReadonlyMap<string, DetailImageUploadStatus>
  >(new Map());

  const setUploading = useCallback(
    (key: string) =>
      setUploadStates((prev) => new Map(prev).set(key, 'uploading')),
    []
  );
  const setError = useCallback(
    (key: string) => setUploadStates((prev) => new Map(prev).set(key, 'error')),
    []
  );
  const clear = useCallback(
    (key: string) =>
      setUploadStates((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      }),
    []
  );

  const value = useMemo(
    () => ({ uploadStates, setUploading, setError, clear }),
    [uploadStates, setUploading, setError, clear]
  );

  return (
    <DetailImageUploadContext.Provider value={value}>
      {children}
    </DetailImageUploadContext.Provider>
  );
};

export const useDetailImageUpload = () => {
  const context = useContext(DetailImageUploadContext);
  if (!context) {
    throw new Error(
      'useDetailImageUpload must be used within DetailImageUploadProvider'
    );
  }
  return context;
};
